import * as React from "react";
import { useRef, useState } from "react";
import { Lightbox, LightboxType } from "../../../../../react-core/Html/Components/Lightbox";
import { JsonDateParser } from "../../../../../react-core/Utils/JsonDateParser";
import { Scroll } from "../../../../../react-core/Utils/Scroll";
import { MultiPropositionType } from "../../../../../Wrap/Constants/Enums/Domain/MultiPropositionType";
import { SubAccountType } from "../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { AdvancedSearchFilterType } from "../../../../../Wrap/Constants/Enums/Search/AdvancedSearchFilterType";
import { UserClaim } from "../../../../../Wrap/Constants/Enums/UserClaim";
import { AccountSearchResult } from "../../../../../Wrap/Models/Models/Transformation/Account/AccountSearchResult";
import AdviserSiteTagManager from "../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { checkPermission } from "../../../../Shared/Permission/PermissionHelper";
import { Navigator } from "../../../../Shared/Wizards/Entities/Navigator";
import { StaticUrls } from "../../../Resoures/StaticUrls";
import { UserStore } from "../../../Stroes/Variable";
import { Urls } from "../../../Urls";
import {
    AccountSearchAction,
    IAdvancedSearchQuery,
    IBasicSearchQuery,
} from "../../Clients/ClientRecord/Action/AccountSearchAction";
import { AccountSearchResultTable } from "../../Clients/ClientSearch/Component/AccountSearchResultTable";
import { BasicSearch } from "../../Clients/ClientSearch/Component/BasicSearch";
import { ToolContent, ToolContentProps } from "../Resource/Content";

export const Tool = () => {
    const [showCGST, setShowCGST] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [data, setData] = useState(null);
    const [query, setQuery] = useState({} as IBasicSearchQuery);
    const [errorMsg, setErrorMsg] = useState(null);

    const searchResults = useRef();

    const { addEvent } = new AdviserSiteTagManager();

    React.useEffect(() => {
        addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("tools")
                    ? window.location.pathname
                    : window.location.pathname + "/tools",
                pageTitle: `${document.title} - tools and literature - tools`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }, []);

    React.useEffect(() => {
        if (data !== null && !isSearching) {
            Scroll.scrollToElement(searchResults.current);
        }
    }, [data, isSearching]);

    const search = (query: IBasicSearchQuery, currentPage, pageSize) => {
        setIsSearching(true);
        const advanceSearch: IAdvancedSearchQuery = {
            wrappers: [SubAccountType.InvestmentAccount, SubAccountType.PersonalPortfolio],
            filterType: AdvancedSearchFilterType.Wrapper,
        };
        setErrorMsg(
            data && data.length > 0
                ? null
                : "No results returned. Please ensure the client holds a Wrap account and that a Personal Portfolio sub account exists"
        );
        AccountSearchAction.searchAccounts(query, [advanceSearch], currentPage, pageSize, (res) => {
            setIsSearching(false);
            setData(res);
        });
    };

    const onBasicSearch = (query) => {
        setQuery(query);
        search(query, 1, 10);
    };

    const onPageChange = (currentPage, pageSize) => {
        search(query, currentPage, pageSize);
    };

    const renderEachBox = (data: ToolContentProps) => {
        return (
            <div className="col-12 col-md-4 col-equalise ">
                <div className="panel-elevated">
                    <div className="mb-30 mt-30">{data?.icon}</div>

                    <h2>{data.title}</h2>
                    <p>{data.content}</p>
                    {data.href && (
                        <div style={{ borderTop: "none", marginTop: "0px", padding: "0px" }}>
                            <div>
                                <a
                                    className="text-left hyperlink text-size-16 inline-block client-portal-view"
                                    href={data.href}
                                    role="link"
                                    target="_blank"
                                >
                                    <span className="icon" aria-hidden="true"></span>
                                    {data.link}
                                </a>
                            </div>
                        </div>
                    )}
                    {data.type == "CGST" && (
                        <button className="hyperlink-next" onClick={() => setShowCGST(true)}>
                            {data.link}
                        </button>
                    )}
                    {data.type == "IST" && (
                        <button
                            className="hyperlink-next"
                            onClick={() => {
                                Navigator.navigateTo(Urls.get().main.ResearchIst);
                            }}
                        >
                            {data.link}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderColSection = (colConfig: ToolContentProps[]) => {
        let index = 0;
        const viewItems = [];
        while (index < colConfig.length) {
            const _data = colConfig.slice(index, index + 3);
            const view = _data.map((item, i) => {
                return renderEachBox(item);
            });
            viewItems.push(<div className="row">{view}</div>);

            index = index + 3;
        }
        return viewItems;
    };

    return (
        <>
            {renderColSection(ToolContent)}
            {showCGST && (
                <Lightbox
                    show={showCGST}
                    title={"Find accounts"}
                    contentAfterTitle={null}
                    onClose={() => {
                        setShowCGST(false);
                    }}
                    type={LightboxType.Modal}
                    unclosable={false}
                    noOverlay={false}
                    noAutofocus={false}
                    cssWrapper={"is-default-lightbox is-wide"}
                >
                    <div className="row">
                        <div className="col-12">
                            <BasicSearch
                                ServicePropositionsToExclude={[
                                    MultiPropositionType.Fundzone,
                                    MultiPropositionType.FZD2C,
                                ]}
                                onSearch={(query) => onBasicSearch(query)}
                                isSearching={isSearching}
                                fromToolAndLiterature={true}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12" ref={searchResults}>
                            <AccountSearchResultTable
                                data={data}
                                errorMsg={errorMsg}
                                onPageChange={(s, c) => onPageChange(s, c)}
                                usingInTools={
                                    checkPermission(UserClaim.AdviserPortalDluCapitalGainsScenarioToolCanView)
                                        ? (row: AccountSearchResult) =>
                                              window.location.assign(
                                                  `/clients/performance/capitalgainsscenariotool.aspx?claccountid=${row.AccountNumber.toLowerCase()}`
                                              )
                                        : () => {
                                              window.location.assign(StaticUrls.EmptyUrl);
                                          }
                                }
                                fromToolAndLiterature={true}
                                basicQuery={query}
                            />
                        </div>
                    </div>
                </Lightbox>
            )}
        </>
    );
};
